import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate, useParams } from "react-router-dom";
import Breadcrumb from "../core/common/Breadcrumb/breadcrumb";
import { OptionType } from "../core/common/commonSelect";
import Table from "../core/common/dataTable/index";
import { AppDispatch, RootState } from "../core/redux/store";
import AddStudentModal from "./components/AddStudentModal";
import {
    attachStudentsToClassroom,
    detachStudentFromClassroom,
    fetchClassroomById,
} from "../core/redux/classroomSlice";

import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { Spinner } from "react-bootstrap";
import { statsService, ApiLevelResponse, ApiTopicStat } from "../core/services/stats.service";
import { SkillData, TopicTableData } from "./home/types";

const Students = () => {
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const { classCode } = useParams();

    const { current } = useSelector((state: RootState) => state.classrooms);
    const { levels } = useSelector((state: RootState) => state.levels);

    const [studentToDelete, setStudentToDelete] = useState<any>(null);
    const [isExporting, setIsExporting] = useState<boolean>(false);

    useEffect(() => {
        if (!classCode) return;
        const id = Number(classCode);
        if (!Number.isFinite(id)) return;
        dispatch(fetchClassroomById({ id }));
    }, [classCode, dispatch]);

    const students = (current?.users || []).map((u: any) => {
        const nameParts = (u.name || "").split(" ");
        const firstName = nameParts[0] || u.name || "";
        const surname = nameParts.length > 1 ? nameParts.slice(1).join(" ") : "";
        const username = u.email ? u.email.split("@")[0] || "" : "";
        return {
            id: u.id,
            name: firstName,
            surname: surname,
            username: username,
            avatar: u.avatar,
            email: u.email,
            Progress: "0%",
        };
    });

    const handleAddStudents = async (selectedStudents: OptionType[]) => {
        if (!classCode || !selectedStudents || selectedStudents.length === 0) return;
        const id = Number(classCode);
        const userIds = selectedStudents.map((s) => s.value as string);
        try {
            await dispatch(attachStudentsToClassroom({ id, userIds })).unwrap();
            await dispatch(fetchClassroomById({ id }));
        } catch (error) {
            console.error("Failed to attach students:", error);
        }
    };

    const handleDeleteStudent = (student: any) => {
        setStudentToDelete(student);
    };

    const confirmDeleteStudent = async () => {
        if (!studentToDelete || !classCode) return;
        const id = Number(classCode);
        try {
            await dispatch(
                detachStudentFromClassroom({ id, userId: studentToDelete.id })
            ).unwrap();
            await dispatch(fetchClassroomById({ id }));
            setStudentToDelete(null);
        } catch (error) {
            console.error("Failed to remove student:", error);
        }
    };

    const fetchStudentChartData = async (studentId: string | number): Promise<SkillData[]> => {
        if (!levels || levels.length === 0) return [];

        const promises = levels.map((level: any) => statsService.getStudentStats(level.value, studentId));
        const responses = await Promise.all(promises);

        const transformedChartData: SkillData[] = [];

        responses.forEach((response: ApiLevelResponse) => {
            if (!response || !response.level) return;

            const levelName = response.level.name;
            const apiTopics = response.level.topics || {};

            const categoryAggregator: Record<
                string,
                { totalContent: number; totalSuccess: number; topicDetails: TopicTableData[]; id: number }
            > = {};

            Object.values(apiTopics).forEach((topic: ApiTopicStat) => {
                const apiCategories = topic.categories || {};

                Object.values(apiCategories).forEach((catStat) => {
                    const catName = catStat.name;

                    if (!categoryAggregator[catName]) {
                        categoryAggregator[catName] = {
                            totalContent: 0,
                            totalSuccess: 0,
                            topicDetails: [],
                            id: catStat.id,
                        };
                    }

                    categoryAggregator[catName].totalContent += catStat.content_count;
                    categoryAggregator[catName].totalSuccess += catStat.successfull_count;

                    if (catStat.content_count > 0) {
                        const successScore = Math.round((catStat.successfull_count / catStat.content_count) * 100);
                        categoryAggregator[catName].topicDetails.push({
                            key: `${topic.id}_${response.level.id}_${catStat.id}`,
                            name: topic.name,
                            totalActivity: catStat.content_count,
                            totalSuccess: catStat.successfull_count,
                            successScore: successScore,
                        });
                    }
                });
            });

            const finalLabels: string[] = [];
            const finalData: number[] = [];
            const finalTotalActivities: number[] = [];
            const finalTotalSuccesses: number[] = [];
            const finalTopicDetails: { [key: string]: TopicTableData[] } = {};

            Object.entries(categoryAggregator).forEach(([catName, data]) => {
                const successRate = data.totalContent > 0 ? (data.totalSuccess / data.totalContent) * 100 : 0;

                finalLabels.push(catName);
                finalData.push(Math.round(successRate));
                finalTotalActivities.push(data.totalContent);
                finalTotalSuccesses.push(data.totalSuccess);
                finalTopicDetails[catName] = data.topicDetails;
            });

            const totalSuccessPercentage = finalData.reduce((a: number, b: number) => a + b, 0);

            if (totalSuccessPercentage > 0 || finalLabels.length > 0) {
                transformedChartData.push({
                    title: levelName,
                    level: levelName,
                    labels: finalLabels,
                    data: finalData,
                    totalActivities: finalTotalActivities,
                    totalSuccesses: finalTotalSuccesses,
                    topicDetails: finalTopicDetails,
                });
            }
        });

        return transformedChartData.filter((level) => {
            const totalSuccessPercentage = level.data.reduce((sum: number, rate: number) => sum + rate, 0);
            return totalSuccessPercentage > 0;
        });
    };

    const buildStudentWorksheet = (worksheet: ExcelJS.Worksheet, studentName: string, chartData: SkillData[]) => {
        if (!chartData || chartData.length === 0) {
            worksheet.columns = [
                { key: 'col1', width: 46 },
                { key: 'col2', width: 18 },
                { key: 'col3', width: 18 },
                { key: 'col4', width: 20 }
            ];
            worksheet.mergeCells('A1:D2');
            const heroCell = worksheet.getCell('A1');
            heroCell.value = `STUDENT PERFORMANCE & ANALYTICS REPORT\n${studentName}`;
            heroCell.font = { name: 'Segoe UI', size: 14, bold: true, color: { argb: 'FFFFFFFF' } };
            heroCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF0F172A' } };
            heroCell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };

            worksheet.mergeCells('A4:D4');
            const noDataCell = worksheet.getCell('A4');
            noDataCell.value = '  No activity data found for this student.';
            noDataCell.font = { name: 'Segoe UI', size: 10, italic: true, color: { argb: 'FF64748B' } };
            return;
        }

        const numLevels = chartData.length;

        for (let i = 0; i < numLevels; i++) {
            const startCol = 1 + i * 7;
            worksheet.getColumn(startCol).width = 46;
            worksheet.getColumn(startCol + 1).width = 18;
            worksheet.getColumn(startCol + 2).width = 18;
            worksheet.getColumn(startCol + 3).width = 20;

            if (i < numLevels - 1) {
                worksheet.getColumn(startCol + 4).width = 4;
                worksheet.getColumn(startCol + 5).width = 4;
                worksheet.getColumn(startCol + 6).width = 4;
            }
        }

        const lastCol = 1 + (numLevels - 1) * 7 + 3;
        worksheet.mergeCells(1, 1, 2, lastCol);
        const heroCell = worksheet.getCell(1, 1);
        heroCell.value = `STUDENT PERFORMANCE & ANALYTICS REPORT\n${studentName}`;
        heroCell.font = { name: 'Segoe UI', size: 14, bold: true, color: { argb: 'FFFFFFFF' } };
        heroCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF0F172A' } };
        heroCell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };

        chartData.forEach((levelData, i) => {
            const startCol = 1 + i * 7;
            let localRow = 4;

            let levelTotalActivity = 0;
            let levelTotalSuccess = 0;

            const categoryDetails = levelData.labels.map((categoryName, index) => {
                const totalActivity = levelData.totalActivities[index] || 0;
                const totalSuccess = levelData.totalSuccesses[index] || 0;
                const successScore = levelData.data[index] || 0;
                const topicDetails = levelData.topicDetails[categoryName] || [];

                levelTotalActivity += totalActivity;
                levelTotalSuccess += totalSuccess;

                return {
                    name: categoryName,
                    totalActivity,
                    totalSuccess,
                    successScore,
                    topicDetails
                };
            }).sort((a, b) => b.totalActivity - a.totalActivity);

            const levelSuccessScore = levelTotalActivity > 0
                ? Math.round((levelTotalSuccess / levelTotalActivity) * 100)
                : 0;

            worksheet.mergeCells(localRow, startCol, localRow, startCol + 3);
            const lvlHeader = worksheet.getCell(localRow, startCol);
            lvlHeader.value = `  LEVEL OVERVIEW SUMMARY: ${levelData.title}`;
            lvlHeader.font = { name: 'Segoe UI', size: 11, bold: true, color: { argb: 'FFFFFFFF' } };
            lvlHeader.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1E293B' } };
            lvlHeader.alignment = { vertical: 'middle' };
            worksheet.getRow(localRow).height = 26;
            localRow++;

            const headers = ['Name', 'Total Activity', 'Total Success', 'Success Score (%)'];
            headers.forEach((h, idx) => {
                const cell = worksheet.getCell(localRow, startCol + idx);
                cell.value = h;
                cell.font = { name: 'Segoe UI', size: 10, bold: true, color: { argb: 'FF334155' } };
                cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF1F5F9' } };
                cell.alignment = { horizontal: 'center', vertical: 'middle' };
                cell.border = { bottom: { style: 'medium', color: { argb: 'FFCBD5E1' } } };
            });
            worksheet.getRow(localRow).height = 24;
            localRow++;

            const summaryNameCell = worksheet.getCell(localRow, startCol);
            summaryNameCell.value = levelData.title;
            summaryNameCell.font = { name: 'Segoe UI', size: 10, bold: true };

            const summaryActCell = worksheet.getCell(localRow, startCol + 1);
            summaryActCell.value = levelTotalActivity;
            summaryActCell.numFmt = '#,##0';

            const summarySucCell = worksheet.getCell(localRow, startCol + 2);
            summarySucCell.value = levelTotalSuccess;
            summarySucCell.numFmt = '#,##0';

            const summaryScoreCell = worksheet.getCell(localRow, startCol + 3);
            summaryScoreCell.value = levelSuccessScore / 100;
            summaryScoreCell.numFmt = '0.0%';

            worksheet.getRow(localRow).height = 24;
            localRow += 3;

            categoryDetails.forEach((cat) => {
                worksheet.mergeCells(localRow, startCol, localRow, startCol + 3);
                const catCell = worksheet.getCell(localRow, startCol);
                catCell.value = `  CATEGORY: ${cat.name}`;
                catCell.font = { name: 'Segoe UI', size: 10, bold: true, color: { argb: 'FFFFFFFF' } };
                catCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF334155' } };
                catCell.alignment = { vertical: 'middle' };
                worksheet.getRow(localRow).height = 24;
                localRow++;

                const topHeaders = ['Topic Breakdown', 'Total Activity', 'Total Success', 'Success Score (%)'];
                topHeaders.forEach((h, idx) => {
                    const cell = worksheet.getCell(localRow, startCol + idx);
                    cell.value = h;
                    cell.font = { name: 'Segoe UI', size: 9, bold: true, color: { argb: 'FF475569' } };
                    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF8FAFC' } };
                    cell.alignment = { horizontal: 'center', vertical: 'middle' };
                });
                localRow++;

                if (cat.topicDetails && cat.topicDetails.length > 0) {
                    cat.topicDetails.forEach((topic: any, idx: number) => {
                        const act = topic.totalActivity || topic.activityCount || 0;
                        const suc = topic.totalSuccess || topic.successCount || 0;
                        const score = act > 0 ? suc / act : 0;

                        const c1 = worksheet.getCell(localRow, startCol);
                        c1.value = `    ▪  ${topic.name || topic.topicName}`;

                        const c2 = worksheet.getCell(localRow, startCol + 1);
                        c2.value = act;
                        c2.numFmt = '#,##0';

                        const c3 = worksheet.getCell(localRow, startCol + 2);
                        c3.value = suc;
                        c3.numFmt = '#,##0';

                        const c4 = worksheet.getCell(localRow, startCol + 3);
                        c4.value = score;
                        c4.numFmt = '0.0%';

                        if (idx % 2 === 1) {
                            [c1, c2, c3, c4].forEach(c => {
                                c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF8FAFC' } };
                            });
                        }

                        worksheet.getRow(localRow).height = 20;
                        localRow++;
                    });
                }
                localRow += 1;
            });
        });
    };

    const handleExportAllToExcel = async () => {
        if (!students || students.length === 0) return;

        if (!levels || levels.length === 0) {
            alert("Level data is not loaded yet. Please try again.");
            return;
        }

        setIsExporting(true);

        try {
            const workbook = new ExcelJS.Workbook();

            for (const student of students) {
                const fullName = `${student.name} ${student.surname}`.trim();
                let safeSheetName = fullName.replace(/[\\/?*[\]]/g, "").substring(0, 31);
                if (!safeSheetName) safeSheetName = `Student_${student.id}`;

                const worksheet = workbook.addWorksheet(safeSheetName, {
                    views: [{ showGridLines: true }],
                });

                try {
                    const chartData = await fetchStudentChartData(student.id);
                    buildStudentWorksheet(worksheet, fullName, chartData);
                } catch (error) {
                    console.error(`Failed to fetch stats for student ${student.id}`, error);
                    buildStudentWorksheet(worksheet, fullName, []);
                }
            }

            const buffer = await workbook.xlsx.writeBuffer();
            const blob = new Blob([buffer], {
                type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            });
            saveAs(blob, `Class_${current?.name?.replace(/[^a-zA-Z0-9_-]/g, "_") || classCode}_Reports.xlsx`);
        } catch (error) {
            console.error("Export failed:", error);
        } finally {
            setIsExporting(false);
        }
    };

    const columns = [
        {
            title: "Student",
            dataIndex: "username",
            render: (username: string, record: any) => (
                <div className="d-flex align-items-center">
                    <Link
                        to={`/student-groups/${classCode}/students/${record.id}`}
                        className="avatar avatar-lg me-2 flex-shrink-0"
                    >
                        <img
                            className="img-fluid object-fit-cover"
                            src={record.avatar ? record.avatar : "/assets/img/favicon.png"}
                            alt={`student ${username}`}
                            style={{ width: "60px", height: "60px", objectFit: "cover" }}
                        />
                    </Link>
                    <div>
                        <h6 className="fw-medium mb-2">
                            <Link to={`/student-groups/${classCode}/students/${record.id}`}>
                                {record.name + " " + record.surname}
                            </Link>
                        </h6>
                        <div className="d-flex align-items-center">
              <span className="d-inline-flex fs-12 align-items-center">
                #{record.username}
              </span>
                        </div>
                    </div>
                </div>
            ),
            sorter: (a: any, b: any) => (a.name + a.surname).localeCompare(b.name + b.surname),
        },
        {
            title: "Email",
            dataIndex: "email",
            render: (text: string) => (
                <div className="d-flex align-items-center">
                    <p className="ms-2">{text}</p>
                </div>
            ),
            sorter: (a: any, b: any) => a.email.localeCompare(b.email),
        },
        {
            title: "Action",
            dataIndex: "",
            render: (text: string, record: any) => (
                <div className="align-items-center">
                    <Link
                        to={`/student-groups/${classCode}/students/${record.id}`}
                        className="d-inline fs-14 action-icon"
                    >
                        <i className="isax isax-eye" /> Report
                    </Link>
                    <button
                        className="d-inline fs-14 action-icon text-danger border-0 bg-transparent"
                        onClick={() => handleDeleteStudent(record)}
                        data-bs-toggle="modal"
                        data-bs-target="#delete_modal"
                    >
                        <i className="isax isax-trash" /> Remove
                    </button>
                </div>
            ),
        },
    ];

    return (
        <>
            <Breadcrumb
                title={current?.name || ""}
                subtitles={[
                    {
                        title: "Cohorts",
                        link: "/student-groups/",
                    },
                ]}
            />
            <div className="content">
                <div className="container">
                    <div className="row">
                        <div className="col-12 mb-3">
                            <button
                                className="btn btn-dark rounded-pill"
                                type="button"
                                onClick={() => navigate("/student-groups/")}
                            >
                                <i className="isax isax-arrow-left-2 me-2"></i>
                                Back
                            </button>
                        </div>
                        <div className="col-lg-12">
                            <div className="page-title d-flex align-items-center justify-content-between">
                                <div>
                                    <h5 className="fw-bold">Students</h5>
                                    <p className="text-muted mb-0">
                                        {students.length} student{students.length !== 1 ? "s" : ""} in this class
                                    </p>
                                </div>
                                <div className="d-flex align-items-center gap-2 list-icons">
                                    <button
                                        className="btn btn-success rounded-pill d-flex align-items-center"
                                        style={{ backgroundColor: "#217346", borderColor: "#217346", width: "100%" }}
                                        onClick={handleExportAllToExcel}
                                        disabled={isExporting}
                                    >
                                        {isExporting ? (
                                            <><Spinner size="sm" className="me-2" /> Exporting...</>
                                        ) : (
                                            <><i className="isax isax-document-download me-2"></i> Export Reports</>
                                        )}
                                    </button>

                                    <button
                                        className="btn btn-secondary rounded-pill d-flex align-items-center"
                                        data-bs-toggle="modal"
                                        data-bs-target="#add_student_modal"
                                        style={{ width: "100%" }}
                                    >
                                        <i className="isax isax-add me-2"></i>
                                        Add Student
                                    </button>
                                </div>
                            </div>
                            <div className="row justify-content-end">
                                <div className="col-md-4">
                                    <div className="input-icon mb-3 invisible">
                    <span className="input-icon-addon">
                      <i className="isax isax-search-normal-14" />
                    </span>
                                        <input
                                            type="email"
                                            className="form-control form-control-md"
                                            placeholder="Search"
                                        />
                                    </div>
                                </div>
                            </div>
                            <Table dataSource={students} columns={columns} Search={true} />
                        </div>
                    </div>
                </div>
            </div>

            <div className="modal fade" id="delete_modal">
                <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content">
                        <div className="modal-body text-center custom-modal-body">
              <span className="avatar avatar-lg bg-danger-transparent rounded-circle mb-2">
                <i className="isax isax-trash fs-24 text-danger" />
              </span>
                            <div>
                                <h4 className="mb-2">Detach Student</h4>
                                <p className="mb-3">
                                    Are you sure you want to detach{" "}
                                    {studentToDelete
                                        ? `${studentToDelete.name} ${studentToDelete.surname}`
                                        : "this student"}{" "}
                                    from this course?
                                </p>
                                <div className="d-flex align-items-center justify-content-between bg-green gap-2">
                                    <button
                                        className="btn bg-gray-100 rounded-pill"
                                        data-bs-dismiss="modal"
                                        onClick={() => setStudentToDelete(null)}
                                    >
                                        Cancel
                                    </button>
                                    <div className="d-flex  gap-2">
                                        <button
                                            className="btn btn-secondary rounded-pill"
                                            data-bs-dismiss="modal"
                                            onClick={confirmDeleteStudent}
                                        >
                                            Detach
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <AddStudentModal classroomId={Number(classCode)} onAddStudents={handleAddStudents} />
        </>
    );
};

export default Students;